/**
 * 지점주 과제 관리 — 콘텐츠 등록·편집 (useReport)
 *
 * Report/ 폴더 트리 전체를 탐색 (강사 조회와 달리 배포 필터 없음)
 * 메뉴 1: 등록 / 메뉴 2: 지점 배포 (ReleaseView)
 */
import { useEffect, useRef, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import { folder } from "shared/types/Report";
import { collection, doc, QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import useFirebaseConfig from "shared/hooks/useFirebaseConfig";
import { createReportWithSettings } from "../reportWrites";

const useReport = () => {
    const { dbFirestore } = useFirebaseConfig();
    const creatingReport = useRef(false);
    const [reportError, setReportError] = useState("");
    const [selectedMenu, setSelectedMenu] = useState<number>(1);
    const {addFireStore, deleteFirestore, updateFirestore, readFirestoreWithSort} = useFirestore();
    const [nowFolder, setNowFolder] = useState<string>("Report");
    const [selectedReport, setSelectedReport] = useState<string>("");
    const [folderList, setFolderList] = useState<folder[]>([]);
    const [selectedFolderList, setSelectedFolderList] = useState<string[]>([]);
    const [isOpenReportModal, setIsOpenReportModal] = useState<boolean>(false);
    const [modalInput, setModalInput] = useState<string>("");
    const [isOpenFolderDeleteModal, setIsOpenFolderDeleteModal] = useState(false);
    
    useEffect(() => {
        getFolderList();
        // eslint-disable-next-line
    }, [nowFolder])

    useEffect(() => {
        setNowFolder("Report"); 
        setSelectedReport("");
    }, [selectedMenu])

    const getFolderList = () => {
        readFirestoreWithSort(nowFolder, {name:"name", direction:"asc"}).then(res => {
            const list: folder[] = res.docs.map((doc: QueryDocumentSnapshot<DocumentData>) => ({
                id: doc.id,
                name: doc.data().name,
                type: doc.data().type,
            }));
            setFolderList(list);
        });
    }

    const onMenuItemClickHandler = (id:number) => {
        setSelectedMenu(id);
        switch(id){
            // 과제등록
            case 1:
                break;
            case 2:
                break;
            case 3:
                break;
            case 4:
                break;
            case 5:
                break;
            default:
                return;
        }
    }
    
    const onSubMenuItemClickHandler = (id:number) => {
        switch(id){
            // 폴더 추가
            case 1:
                // if(nowFolder !== "Report"){
                //     onCreateReportClick();
                //     return;
                // }
                onCreateFolderClick();
                break;
            case 2:
                onCreateReportClick();
                break;
            case 3:
                onRenameFolderClickHandler();
                break;
            case 4:
                onDeleteFolderClickHandler();
                break;
            default:
                return;
        }
    }


    const onCreateFolderClick = async() => {
        let folderName = "새폴더";
        let i = 1;
        while(true){
            const isExists = folderList.some(item => item.name === folderName);
            if(isExists){
                folderName += i.toString();
                i++;
            }
            if(!isExists){
                break;
            }
        }

        const id = await addFireStore(nowFolder, {
            name:folderName,
            type:"folder"
        }).then(res => {
            return res;
        })
        if(id){
            setFolderList(prev => [...prev, {id:id, name:folderName, type:"folder"}])
        }
    }

    const onCreateReportClick = async() => {
        if (creatingReport.current) return;
        creatingReport.current = true;
        try {
        let report_name = "과제";
        let i = 1;
        while(true){
            const isExists = folderList.some(item => item.name === report_name);
            if(isExists){
                report_name += i.toString();
                i++;
            }
            if(!isExists){
                break;
            }
        }
        const id = doc(collection(dbFirestore, nowFolder)).id;
        await createReportWithSettings(dbFirestore, `${nowFolder}/${id}`, report_name);
        setFolderList(prev => [...prev, {id, name:report_name, type:"report"}]);
        } catch (error) {
            console.error("과제 생성 실패:", error);
            setReportError("과제와 학습설정을 저장하지 못했습니다. 다시 시도해주세요.");
        } finally {
            creatingReport.current = false;
        }
    }

    const onFolderClick = (folder:folder) => {
        if(folder.type === "folder"){
            setNowFolder(prev => prev + "/" + folder.id + "/Report");
        }else{
            setSelectedReport(folder.id);
        }
    }

    const onPrevClickHandler = () => {
        // 현재가 최상위 폴더인 경우
        if (nowFolder === "Report") {
            return;
        }
        
        // 현재 경로에서 마지막 "/Report"를 제거
        const pathWithoutLastReport = nowFolder.slice(0, -7); // "/Report" 길이는 7
        
        // 마지막 폴더 ID를 찾아서 제거
        const lastSlashIndex = pathWithoutLastReport.lastIndexOf('/');
        
        if (lastSlashIndex === -1) {
            // 더 이상 상위 폴더가 없으면 최상위로
            setNowFolder("Report");
        } else {
            // 이전 폴더 경로로 설정
            setNowFolder(pathWithoutLastReport.slice(0, lastSlashIndex));
        }
        
        setSelectedReport("");
    }

    const onFolderSelectClickHandler = (id:string, value:boolean) => {
        setSelectedFolderList(prev => {
            if (value) {
                return [...prev, id];
            } else {
                return prev.filter(selectedId => selectedId !== id);
            }
        });
    }

    const onFolderSelectAllClickHandler = (value: boolean) => {
        let id_list: string[] = [];
        if (value) {
            id_list = folderList.map((item) => item.id);
        }
        setSelectedFolderList(id_list);
    };

    const onDeleteFolderClickHandler = async() => {
       setIsOpenFolderDeleteModal(true);
    }

    const onFolderDeleteConfirmClickHandler = async() => {
        if(!selectedFolderList.length){
            return;
        }
        for(let i = 0; i < selectedFolderList.length; i++){
            const result = await deleteFirestore(nowFolder || "Report", selectedFolderList[i]).then(res => {
                return res;
            })
            if(result){
                setFolderList(prev => prev.filter(item => item.id !== selectedFolderList[i]));
            }
        }
        setIsOpenFolderDeleteModal(false);
    }

    const onFolderDeleteCancelClickHandler = () => {
        setIsOpenFolderDeleteModal(false);
    }
    
    const onRenameFolderClickHandler = async() => {
        if(nowFolder === "Report"){
            if(selectedFolderList.length !== 1){
                return;
            }
        }
        else{
            if(selectedFolderList.length !== 1 && !selectedReport){
                return;
            }
        }
        const id = selectedReport || selectedFolderList[0];
        const folder = folderList.find(item => item.id === id);
        if(folder){
            setModalInput(folder.name);
            setIsOpenReportModal(true);
        }
    }

    const onReportModalConfirmClickHandler = async() => {
        const id = selectedReport || selectedFolderList[0];
        const result = await updateFirestore(nowFolder, id, {
            name:modalInput
        }).then(res => {
            return res;
        })

        if(result){
            let prev_folder = folderList.find(item => item.id === id);
            if(prev_folder){
                const new_folder = {...prev_folder, name:modalInput};
                setFolderList(prevList => prevList.map(folder => 
                    folder.id === id ? new_folder : folder
                ));
            }
        }
        setIsOpenReportModal(false);
        setSelectedFolderList([]);
    }

    const onReportModalCancelClickHandler = async() => {
        setIsOpenReportModal(false);
    }

    const onReportModalInputChangeHandler = (value:string) => {
        setModalInput(value);
    }

    return{reportError, clearReportError: () => setReportError(""), selectedMenu, folderList, onMenuItemClickHandler, onSubMenuItemClickHandler, onFolderClick, nowFolder,
      onPrevClickHandler, selectedReport, onFolderSelectClickHandler, onFolderSelectAllClickHandler, selectedFolderList, onDeleteFolderClickHandler,
      isOpenReportModal, modalInput, onReportModalInputChangeHandler, onReportModalConfirmClickHandler, onReportModalCancelClickHandler, isOpenFolderDeleteModal, onFolderDeleteConfirmClickHandler, onFolderDeleteCancelClickHandler
    }

}

export default useReport;
